import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Building2, FileText } from 'lucide-react';
import { colors, Card, EmptyState, LoadingSpinner, NotesSlideshow } from '../../shared';
import { useData } from '../../../contexts/DataContext';
import { INSPECTION_TYPES } from '../../../data/inspectionTypes';
import { analyzeHgrOnboarding, formatOnboardDuration } from '../../../utils/highgroveOnboarding';

const ONBOARDING = INSPECTION_TYPES.onboarding;

const STATUS_STYLE = {
  none: { label: 'No inspection', bg: colors.redDim, color: colors.red },
  partial: { label: 'Partial', bg: colors.orangeDim, color: colors.orange },
  complete: { label: 'Complete', bg: colors.greenDim, color: colors.green },
};

const StatCard = ({ label, value, color }) => (
  <Card>
    <p className="text-sm mb-1" style={{ color: colors.textSecondary }}>{label}</p>
    <p className="text-3xl font-bold" style={{ color: color || colors.textPrimary }}>{value}</p>
  </Card>
);

export default function HighgroveTab({ permissions }) {
  const { units, inspections, loading } = useData();
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  const [notesRow, setNotesRow] = useState(null); // row whose notes slideshow is open, null = closed

  const { rows, buckets, stepFunnel, total, sizeBuckets } = useMemo(
    () => analyzeHgrOnboarding(units, inspections, ONBOARDING),
    [units, inspections]
  );

  // Funnel keeps step order (it's a process, not a ranking) so you can read
  // exactly where units stall. Colored yellow→red as completion drops off.
  const funnelOption = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}<br/>{c} units' },
    color: [colors.green, colors.yellow, colors.orange, colors.red],
    series: [{
      type: 'funnel',
      sort: 'none',
      top: 8,
      bottom: 8,
      left: '2%',
      right: '2%',
      minSize: '30%',
      maxSize: '100%',
      gap: 2,
      label: {
        show: true,
        position: 'inside',
        color: colors.black,
        fontWeight: 600,
        formatter: '{b}: {c}',
      },
      labelLine: { show: false },
      itemStyle: { borderColor: colors.black, borderWidth: 1 },
      data: stepFunnel.map((s) => ({ name: s.title, value: s.count })),
    }],
  }), [stepFunnel]);

  if (!permissions?.viewAnalytics) {
    return (
      <div className="p-6">
        <EmptyState icon={Building2} title="Access Denied" description="You don't have permission to view analytics" />
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }

  const visibleRows = showIncompleteOnly ? rows.filter((r) => r.status !== 'complete') : rows;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>Highgrove Onboarding</h1>
        <p className="text-sm" style={{ color: colors.textSecondary }}>
          Progress across {total} units we actively onboard · listing all {rows.length} HGR-tagged units
        </p>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Building2} title="No HGR units" description="No units are tagged HGR yet." />
      ) : (
        <>
          {/* Status buckets */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Onboarding units" value={total} color={colors.yellow} />
            <StatCard label="No inspection" value={buckets.none} color={colors.red} />
            <StatCard label="Partial" value={buckets.partial} color={colors.orange} />
            <StatCard label="Complete" value={buckets.complete} color={colors.green} />
          </div>

          {/* Overall onboarding progress across the cohort */}
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>Onboarding progress</h2>
              <span className="text-sm font-semibold" style={{ color: colors.green }}>
                {total ? Math.round((buckets.complete / total) * 100) : 0}% complete
              </span>
            </div>
            <div className="flex h-3 rounded-full overflow-hidden" style={{ background: colors.inputBg }}>
              <div style={{ width: `${total ? (buckets.complete / total) * 100 : 0}%`, background: colors.green }} />
              <div style={{ width: `${total ? (buckets.partial / total) * 100 : 0}%`, background: colors.orange }} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: colors.textMuted }}>
              <span style={{ color: colors.green }}>● {buckets.complete} complete</span>
              <span style={{ color: colors.orange }}>● {buckets.partial} partial</span>
              <span style={{ color: colors.red }}>● {buckets.none} not started</span>
              <span className="ml-auto">of {total} units</span>
            </div>
          </Card>

          {/* Per-step funnel */}
          <Card>
            <h2 className="text-lg font-bold mb-2" style={{ color: colors.textPrimary }}>Units per onboarding step</h2>
            <p className="text-xs mb-2" style={{ color: colors.textMuted }}>
              How many units have addressed (done or skipped) each step.
            </p>
            <ReactECharts option={funnelOption} style={{ height: 360 }} notMerge lazyUpdate />
          </Card>

          {/* Onboarding time by property size — completedAt − createdAt, grouped
              per property then bucketed by how many units the property has. */}
          <Card>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>Onboarding time by property size</h2>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  Average time to complete a whole property — the sum of its units' onboard times (completed − created). Only properties where every unit is onboarded count toward the average.
                </p>
              </div>
              <span className="text-sm font-semibold whitespace-nowrap" style={{ color: colors.yellow }}>
                {formatOnboardDuration(sizeBuckets.avgMs)} avg
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <th className="text-left py-2 px-2" style={{ color: colors.textMuted }}>Property size</th>
                    <th className="text-center py-2 px-2" style={{ color: colors.textMuted }}>Properties</th>
                    <th className="text-center py-2 px-2" style={{ color: colors.textMuted }}>Completed</th>
                    <th className="text-right py-2 px-2" style={{ color: colors.textMuted }}>Avg to complete</th>
                  </tr>
                </thead>
                <tbody>
                  {sizeBuckets.buckets.map((b) => (
                    <tr key={b.size} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td className="py-2 px-2" style={{ color: colors.textPrimary }}>
                        {`${b.size} unit${b.size === '1' ? '' : 's'}`}
                      </td>
                      <td className="text-center py-2 px-2" style={{ color: colors.textSecondary }}>{b.propertyCount}</td>
                      <td className="text-center py-2 px-2" style={{ color: colors.textSecondary }}>{b.completeProperties}</td>
                      <td className="text-right py-2 px-2 font-medium" style={{ color: colors.textPrimary }}>{formatOnboardDuration(b.avgMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Unit list, sorted by note count */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
                  Units by notes
                </h2>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  Dimmed units aren't in the onboarding cohort — listed for reference, not counted in progress.
                </p>
              </div>
              <button
                onClick={() => setShowIncompleteOnly((v) => !v)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: showIncompleteOnly ? colors.yellowDim : colors.inputBg,
                  color: showIncompleteOnly ? colors.yellow : colors.textMuted,
                }}
              >
                {showIncompleteOnly ? 'Showing needs onboarding' : 'Show needs onboarding'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <th className="text-left py-3 px-2" style={{ color: colors.textMuted }}>Unit</th>
                    <th className="text-center py-3 px-2" style={{ color: colors.textMuted }}>Notes</th>
                    <th className="text-center py-3 px-2" style={{ color: colors.textMuted }}>Status</th>
                    <th className="text-right py-3 px-2" style={{ color: colors.textMuted }}>Time to complete</th>
                    <th className="text-right py-3 px-2" style={{ color: colors.textMuted }}>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8" style={{ color: colors.textMuted }}>No units</td></tr>
                  ) : visibleRows.map((r) => {
                    const s = STATUS_STYLE[r.status];
                    return (
                      // Non-cohort HGR units are listed for reference but dimmed —
                      // they don't count toward the progress stats above.
                      <tr key={r.unitId} style={{ borderBottom: `1px solid ${colors.border}`, opacity: r.onboarding ? 1 : 0.4 }}>
                        <td className="py-3 px-2">
                          <p className="font-medium" style={{ color: colors.textPrimary }}>{r.address || `Unit ${r.unitId}`}</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>{r.property}</p>
                        </td>
                        <td className="text-center py-3 px-2">
                          {r.noteSlides.length > 0 ? (
                            <button
                              onClick={() => setNotesRow(r)}
                              className="inline-flex items-center gap-1 hover:opacity-70 transition-opacity"
                              style={{ color: colors.textPrimary }}
                              title="View notes"
                            >
                              <FileText size={14} /> {r.noteCount}
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1" style={{ color: colors.textMuted }}>
                              <FileText size={14} /> {r.noteCount}
                            </span>
                          )}
                        </td>
                        <td className="text-center py-3 px-2">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: s.bg, color: s.color }}>
                            {s.label}
                          </span>
                        </td>
                        <td className="text-right py-3 px-2" style={{ color: colors.textSecondary }}>
                          {formatOnboardDuration(r.durationMs)}
                        </td>
                        <td className="text-right py-3 px-2" style={{ color: colors.textSecondary }}>
                          {r.done}/{r.total}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {notesRow && (
        <NotesSlideshow slides={notesRow.noteSlides} onClose={() => setNotesRow(null)} />
      )}
    </div>
  );
}
